// Messaging Layout Component
// Following existing patterns from Dashboard layout

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessagingLayoutProps } from '../../../types/messaging';
import {
  MessagingContainer,
  MessagingHeader,
  MessagingHeaderTitle,
  MessagingHeaderActions,
  MessagingContent,
  MessagingSidebar,
  MessagingMain,
  MobileBackButton,
  MobileOverlay
} from '../../../styles/Messaging';

export const MessagingLayout: React.FC<MessagingLayoutProps> = ({
  children,
  currentConversation,
  onConversationSelect
}) => {
  const navigate = useNavigate();
  // Extract ConversationList and other components from children
  const childrenArray = React.Children.toArray(children);
  const conversationList = childrenArray.find(child =>
    React.isValidElement(child) &&
    (child.type as any)?.displayName === 'ConversationList'
  );
  const otherComponents = childrenArray.filter(child =>
    !(React.isValidElement(child) &&
      (child.type as any)?.displayName === 'ConversationList')
  );
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set viewport height for mobile Safari
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  // On mobile, show sidebar when no conversation is selected
  useEffect(() => {
    if (isMobile) {
      setShowSidebar(!currentConversation);
    }
  }, [isMobile, currentConversation]);

  const handleBackToConversations = () => {
    // Clear the current conversation to go back to conversation list
    if (onConversationSelect) {
      onConversationSelect(null);
    }
    setShowSidebar(true);
  };

  const handleConversationSelect = (conversation: any) => {
    if (onConversationSelect) {
      onConversationSelect(conversation);
    }
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const handleOverlayClick = () => {
    setShowSidebar(false);
  };



  return (
    <MessagingContainer>
      <MessagingHeader>
        {isMobile && currentConversation && (
          <MobileBackButton onClick={handleBackToConversations}>
            ←
          </MobileBackButton>
        )}
        <MessagingHeaderTitle>
          {isMobile && currentConversation
            ? currentConversation.participants?.find((p: any) => p.id !== 'user-1')?.name || 'Chat'
            : 'Message Notifications'
          }
        </MessagingHeaderTitle>
        <MessagingHeaderActions>
          {/* Action buttons removed */}
        </MessagingHeaderActions>
      </MessagingHeader>

      <MessagingContent>
        <MobileOverlay $isVisible={isMobile && showSidebar} onClick={handleOverlayClick} />

        <MessagingSidebar $isVisible={showSidebar}>
          {conversationList}
        </MessagingSidebar>

        <MessagingMain>
          {otherComponents}
        </MessagingMain>
      </MessagingContent>
    </MessagingContainer>
  );
};
